/**
 * Distribute › Invitations (R7, screen 17): one card per batch — name,
 * status, date and people, "Remind unfinished", and the three bars (Sent ·
 * Opened · Finished) — over a footnote on the sending pace. "+ New
 * invitation" opens a right-hand panel: who (pasted, one per line, or a
 * CSV), the subject and message with the {{name}} merge, the reminder
 * schedule, then Send — or Save as draft, which lands on the batch where a
 * test send, the list and Start live as they always did.
 *
 * Sends go through the PLATFORM'S outgoing address, so a batch drains at the
 * platform-wide pace (the footnote says so) rather than all at once: that is
 * what keeps the shared address inside Gmail's bulk-sender limits. A
 * reminder is a row in the same queue (069), so it drains at the same pace.
 *
 * One route, two views: the batch list, and one batch. Which batch is open
 * is local state, not a sub-route — the workspace's tab row stays the URL's
 * story.
 *
 * The page is full-bleed (R7 follow-up): it runs edge to edge under the
 * sub-tabs with the design's fluid padding, and on a desktop the New
 * invitation panel is a 400px column beside the list — the prototype's
 * `<aside>` — rather than an overlay; below 1100 it is the Drawer sheet.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  InvitationBatch, InvitationBatchCounts, InvitationBatchRow, InvitationBatchStatus, InvitationRecipient, InvitationReminder,
  MAX_REMINDER_DAY, MAX_REMINDER_STEPS,
  createInvitationBatchApi, deleteInvitationBatchApi, getInvitationBatchApi, listInvitationBatchesApi,
  pauseInvitationBatchApi, remindInvitationBatchApi, removeInvitationRecipientApi, startInvitationBatchApi,
  testInvitationBatchApi, updateInvitationBatchApi, uploadInvitationCsvApi,
} from '../../api/invitations';
import type { SendWindow } from '../../api/marketing';
import { Drawer, RowMenu, SectionLabel, Toggle } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { DESKTOP, useMediaQuery } from '../../shared/media';
import { parsePastedRecipients, recipientsToCsv } from '../../shared/recipientsPaste';
import { useFormWorkspace } from './FormWorkspace';
import { usePageHeight } from './workflow/usePageHeight';

const STATUS_PILL: Record<InvitationBatchStatus, string> = {
  draft: 'neutral', sending: 'ok', paused: 'caution', done: 'neutral',
};
/** The design's words for the four real states. */
const STATUS_WORD: Record<InvitationBatchStatus, string> = {
  draft: 'Draft', sending: 'Sending', paused: 'Paused', done: 'Finished',
};

const Pill = ({ status }: { status: InvitationBatchStatus }) =>
  <span className={`fs-pill ${STATUS_PILL[status]}`}>{STATUS_WORD[status]}</span>;

const when = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

const START_TOAST = 'Invitations are on their way — they drain at the platform pace.';

/** The pace, told before anybody asks why 500 invitations are not out yet. */
function PaceNote({ window: w }: { window: SendWindow }) {
  return (
    <p className="faint inv-pace">
      Invitations go out at a steady pace — {w.hourlyCap} an hour at most, {w.dailyCap} a day, shared across the whole
      platform ({w.lastHour} this hour, {w.lastDay} today so far) — so they stay out of spam folders. A big list simply
      keeps draining until it is done; reminders join the same queue.
    </p>
  );
}

/** One of the three bars: Sent (ink) · Opened (light accent) · Finished (accent). */
function Bar({ label, value, total, tone }: { label: string; value: number; total: number; tone: 'ink' | 'soft' | 'accent' }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className="inv-bar">
      <span className="inv-bar-label">{label}</span>
      <div className="inv-bar-track" role="progressbar" aria-label={label} aria-valuenow={value} aria-valuemin={0} aria-valuemax={total}>
        <div className={`inv-bar-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Bars({ c }: { c: InvitationBatchCounts }) {
  return (
    <div className="inv-bars">
      <Bar label={`Sent ${c.sent}/${c.total}`} value={c.sent} total={c.total} tone="ink" />
      <Bar label={`Opened ${c.opened}`} value={c.opened} total={c.total} tone="soft" />
      <Bar label={`Finished ${c.submitted}`} value={c.submitted} total={c.total} tone="accent" />
    </div>
  );
}

/** "Reminders on day 3 and day 7" — how a schedule reads. */
export function scheduleSentence(days: number[]): string {
  if (!days.length) return 'No reminders';
  return `Reminders on ${days.map((d) => `day ${d}`).join(days.length === 2 ? ' and ' : ', ').replace(/, (day \d+)$/, ' and $1')}`;
}

/** What the reminder counts add up to under a card. */
function reminderLine(b: InvitationBatchRow): string | null {
  const bits: string[] = [];
  if (b.reminder_days?.length) bits.push(scheduleSentence(b.reminder_days).toLowerCase());
  if (b.reminded) bits.push(`${people(b.reminded)} reminded`);
  if (b.reminders_queued) bits.push(`${b.reminders_queued} reminder${b.reminders_queued === 1 ? '' : 's'} queued`);
  return bits.length ? bits.join(' · ') : null;
}

export default function FormInvitations() {
  const { form } = useFormWorkspace();
  const toast = useToast();
  const [batches, setBatches] = useState<InvitationBatchRow[]>([]);
  const [window_, setWindow] = useState<SendWindow | null>(null);
  const [smtpOk, setSmtpOk] = useState(true);
  const [published, setPublished] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  /** ≥1100 the panel is a column beside the list; the pair then fills the viewport and the list scrolls. */
  const desktop = useMediaQuery(DESKTOP);
  const pageRef = useRef<HTMLDivElement>(null);
  usePageHeight(pageRef, desktop && creating, openId === null);

  const load = () => listInvitationBatchesApi(form.id)
    .then((r) => { setBatches(r.batches || []); setWindow(r.window); setSmtpOk(r.smtpConfigured); setPublished(r.published); })
    .catch((e) => setError((e as Error).message));
  useEffect(() => { void load(); }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn: () => Promise<unknown>, done: string | ((r: any) => string)) => {
    setError('');
    try { const r = await fn(); toast(typeof done === 'function' ? done(r) : done); await load(); } catch (e) { setError((e as Error).message); }
  };

  const remindToast = (r: { queued: number; unfinished: number; skipped: number; paused: boolean }) => {
    if (!r.unfinished) return 'Nobody to remind — everyone the email reached has finished.';
    if (!r.queued) return `Nobody reminded: ${people(r.skipped)} already had a reminder in the last day.`;
    const held = r.paused ? ' They go out when the batch resumes.' : ' They go out at the platform pace.';
    const skipped = r.skipped ? ` ${r.skipped} skipped — reminded in the last day.` : '';
    return `Reminding ${people(r.queued)} who haven’t finished.${held}${skipped}`;
  };

  if (openId !== null) {
    return (
      <BatchDetail
        formId={form.id} batchId={openId} formName={form.name}
        onBack={() => { setOpenId(null); void load(); }}
      />
    );
  }

  const panel = creating && (
    <NewInvitationPanel
      formId={form.id} formName={form.name} smtpOk={smtpOk} published={published} inline={desktop}
      onClose={() => { setCreating(false); void load(); }}
      onDraft={(b) => { setCreating(false); setOpenId(b.id); }}
      onSent={() => { setCreating(false); toast(START_TOAST); void load(); }}
    />
  );

  return (
    <div ref={pageRef} className={`inv-page${creating && desktop ? ' with-panel' : ''}`}>
      <div className="inv-main">
      <div className="fs-page-head">
        <div className="text">
          <h2>Invitations</h2>
          <div className="fs-page-sub">
            Everyone gets their own private link, so you can see who opened it and who finished.
          </div>
        </div>
        <button className="fs-btn primary" onClick={() => setCreating(true)}>+ New invitation</button>
      </div>
      {!smtpOk && (
        <div className="ad-err">Email is not set up on this platform yet, so invitations cannot be sent.</div>
      )}
      {!published && (
        <p className="faint" style={{ fontSize: 13, marginTop: 0 }}>
          This form is not published yet — you can draft an invitation and its list now, and send it once the form is live.
        </p>
      )}
      {error && <div className="ad-err">{error}</div>}

      <div className="inv-cards">
        {batches.map((b) => {
          const note = reminderLine(b);
          const canRemind = b.status !== 'draft' && smtpOk && published && b.unfinished > 0;
          const start = () => { setOpenMenuId(null); void act(() => startInvitationBatchApi(form.id, b.id), START_TOAST); };
          const remind = () => { setOpenMenuId(null); void act(() => remindInvitationBatchApi(form.id, b.id), remindToast); };
          return (
            <section className="fs-card inv-card" key={b.id} aria-label={b.subject}>
              <div className="inv-card-head">
                <a className="inv-name" href="#open" onClick={(e) => { e.preventDefault(); setOpenId(b.id); }}>{b.subject}</a>
                <Pill status={b.status} />
                <span className="inv-meta">{when(b.created_at)} · {people(b.total)}</span>
                {b.status !== 'draft' && (
                  <button
                    className="fs-btn text inv-remind" onClick={remind} disabled={!canRemind}
                    title={!smtpOk ? 'Email is not set up on this platform.' : !published ? 'Publish the form first.' : !b.unfinished ? 'Everyone the email reached has finished.' : `Email ${people(b.unfinished)} who haven’t finished`}
                  >
                    Remind unfinished{b.unfinished ? ` · ${b.unfinished}` : ''}
                  </button>
                )}
                <RowMenu open={openMenuId === b.id} onToggle={(o) => setOpenMenuId(o ? b.id : null)} label={b.subject}>
                  <button onClick={() => { setOpenMenuId(null); setOpenId(b.id); }}>Open</button>
                  {(b.status === 'draft' || b.status === 'paused') && (
                    <button disabled={!smtpOk || !published || !b.total} onClick={start}>
                      {b.status === 'paused' ? 'Resume sending' : 'Start sending'}
                    </button>
                  )}
                  {b.status === 'sending' && (
                    <button onClick={() => { setOpenMenuId(null); void act(() => pauseInvitationBatchApi(form.id, b.id), 'Paused.'); }}>Pause</button>
                  )}
                  {b.status !== 'draft' && <button disabled={!canRemind} onClick={remind}>Remind unfinished</button>}
                  {b.status !== 'sending' && (
                    <>
                      <div className="rule" />
                      <button className="danger" onClick={() => { setOpenMenuId(null); void act(() => deleteInvitationBatchApi(form.id, b.id), 'Invitation deleted.'); }}>Delete</button>
                    </>
                  )}
                </RowMenu>
              </div>
              <Bars c={b} />
              {(note || b.failed > 0 || b.pending > 0) && (
                <div className="inv-note faint">
                  {[
                    b.pending > 0 ? `${b.pending} queued` : null,
                    b.failed > 0 ? `${b.failed} failed` : null,
                    note,
                  ].filter(Boolean).join(' · ')}
                </div>
              )}
            </section>
          );
        })}
        {!batches.length && (
          <section className="fs-card inv-empty faint">
            No invitations yet. Paste a list of people or upload a CSV, send yourself a test, then send — handy for surveys and anything else you send to a list.
          </section>
        )}
      </div>
      {window_ && <PaceNote window={window_} />}
      </div>
      {creating && desktop ? <aside className="inv-aside" aria-label="New invitation">{panel}</aside> : panel}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The reminder schedule: up to three days after each person's first email.
// ---------------------------------------------------------------------------

export function ReminderSchedule({ value, onChange, idPrefix = 'inv-rem' }: {
  value: number[];
  onChange: (days: number[]) => void;
  idPrefix?: string;
}) {
  const on = value.length > 0;
  const setDay = (i: number, raw: string) => {
    const n = Number(raw.replace(/[^0-9]/g, ''));
    onChange(value.map((d, j) => (j === i ? (Number.isFinite(n) ? Math.max(0, Math.min(MAX_REMINDER_DAY, n)) : d) : d)));
  };
  const nextDay = () => {
    const last = value[value.length - 1] || 0;
    return Math.min(MAX_REMINDER_DAY, last + 4);
  };
  return (
    <div className="inv-schedule">
      <div className="la-toggle-row">
        <div className="text">
          <b>Remind people who haven’t finished</b>
          <small>
            {on
              ? 'On the days below, counted from each person’s first email. Whoever has finished is left alone.'
              : 'Off — nobody is nudged. You can still remind everybody unfinished by hand from the batch.'}
          </small>
        </div>
        <Toggle id={`${idPrefix}-on`} checked={on} onChange={(next) => onChange(next ? [3, 7] : [])} />
      </div>
      {on && (
        <div className="inv-days">
          {value.map((d, i) => (
            <label key={i} className="inv-day">
              <span>day</span>
              <input
                id={`${idPrefix}-day-${i}`} inputMode="numeric" aria-label={`Reminder ${i + 1}, day`}
                value={d || ''} onChange={(e) => setDay(i, e.target.value)}
              />
              <button type="button" className="fc-x" title="Remove this reminder" aria-label={`Remove reminder ${i + 1}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button>
            </label>
          ))}
          {value.length < MAX_REMINDER_STEPS && (
            <button type="button" className="fs-btn neutral sm" onClick={() => onChange([...value, nextDay()])}>+ Add a day</button>
          )}
          <div className="hint">Up to {MAX_REMINDER_STEPS} reminders, each 1–{MAX_REMINDER_DAY} days after the first email; nobody gets two in one day.</div>
        </div>
      )}
    </div>
  );
}

/** The schedule as the server will take it, or the reason it will not. */
export function scheduleProblem(days: number[]): string | null {
  if (days.length > MAX_REMINDER_STEPS) return `At most ${MAX_REMINDER_STEPS} reminders.`;
  if (days.some((d) => !Number.isInteger(d) || d < 1 || d > MAX_REMINDER_DAY)) return `Each reminder is 1–${MAX_REMINDER_DAY} days after the first email.`;
  if (new Set(days).size !== days.length) return 'Two reminders on the same day would be one reminder.';
  return null;
}

// ---------------------------------------------------------------------------
// + New invitation: the right-hand panel.
// ---------------------------------------------------------------------------

function NewInvitationPanel({ formId, formName, smtpOk, published, inline = false, onClose, onDraft, onSent }: {
  formId: number; formName: string; smtpOk: boolean; published: boolean;
  /** rendered as the page's own right-hand column (≥1100) rather than a Drawer */
  inline?: boolean;
  onClose: () => void;
  /** saved as a draft: land on the batch, where the list, a test and Start live */
  onDraft: (b: InvitationBatch) => void;
  onSent: () => void;
}) {
  const toast = useToast();
  const [pasted, setPasted] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [subject, setSubject] = useState(`You’re invited to fill in “${formName}”`);
  const [message, setMessage] = useState(
    `Hi {{name}},\n\nWe’d love a few minutes of your time — please fill in “${formName}”. The button below opens your own copy of the form.\n\nThank you!`
  );
  const [days, setDays] = useState<number[]>([3, 7]);
  const [busy, setBusy] = useState<'' | 'test' | 'draft' | 'send'>('');
  const [error, setError] = useState('');
  /** The draft the panel has made so far — created lazily, once, and reused. */
  const batchRef = useRef<InvitationBatch | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const parsed = useMemo(() => parsePastedRecipients(pasted), [pasted]);
  const count = parsed.recipients.length;
  const wordingOk = !!subject.trim() && !!message.trim();
  const schedule = scheduleProblem(days);
  const haveList = count > 0 || files.length > 0;
  const cannotSend = !smtpOk ? 'Email is not set up on this platform yet.'
    : !published ? 'Publish the form first — save this as a draft meanwhile.'
      : !haveList ? 'Add at least one person.'
        : !wordingOk ? 'The email needs a subject and a message.'
          : schedule;

  /** The batch, made on first use and kept in step with the wording since. */
  async function ensureBatch(): Promise<InvitationBatch> {
    const b = batchRef.current;
    if (!b) {
      const r = await createInvitationBatchApi(formId, { subject: subject.trim(), message: message.trim(), reminderDays: days });
      batchRef.current = r.batch;
      return r.batch;
    }
    if (b.subject !== subject.trim() || b.message !== message.trim() || JSON.stringify(b.reminder_days || []) !== JSON.stringify(days)) {
      const r = await updateInvitationBatchApi(formId, b.id, { subject: subject.trim(), message: message.trim(), reminderDays: days });
      batchRef.current = r.batch;
      return r.batch;
    }
    return b;
  }

  /** The list: the paste box as one CSV, then each file, through the one upload route. */
  async function uploadList(b: InvitationBatch): Promise<string> {
    let added = 0, already = 0, invalid = parsed.invalid;
    if (count) {
      const r = await uploadInvitationCsvApi(formId, b.id, new Blob([recipientsToCsv(parsed.recipients)], { type: 'text/csv' }));
      added += r.added; already += r.alreadyOnList; invalid += r.invalid;
    }
    for (const f of files) {
      const r = await uploadInvitationCsvApi(formId, b.id, f);
      added += r.added; already += r.alreadyOnList; invalid += r.invalid;
    }
    return `${added} added${already ? `, ${already} already on the list` : ''}${invalid ? `, ${invalid} unusable row${invalid === 1 ? '' : 's'}` : ''}.`;
  }

  const run = async (what: 'test' | 'draft' | 'send') => {
    if (busy) return;
    if (!wordingOk) { setError('The email needs a subject and a message.'); return; }
    if (schedule) { setError(schedule); return; }
    setBusy(what); setError('');
    try {
      const b = await ensureBatch();
      if (what === 'test') {
        const r = await testInvitationBatchApi(formId, b.id);
        toast(`Test sent to ${r.to}.`);
      } else if (what === 'draft') {
        if (haveList) toast(await uploadList(b));
        onDraft(b);
      } else {
        const summary = await uploadList(b);
        await startInvitationBatchApi(formId, b.id);
        toast(summary);
        onSent();
      }
    } catch (e) { setError((e as Error).message); }
    setBusy('');
  };

  const foot = (
    <div className="inv-drawer-foot">
      <button className="fs-btn primary" disabled={!!busy || !!cannotSend} title={cannotSend || undefined} onClick={() => void run('send')}>
        {busy === 'send' ? 'Sending…' : `Send to ${people(count)}${files.length ? ' + CSV' : ''}`}
      </button>
      <button className="fs-btn neutral" disabled={!!busy || !wordingOk} onClick={() => void run('draft')}>
        {busy === 'draft' ? 'Saving…' : 'Save as draft'}
      </button>
      <button className="fs-btn text" disabled={!!busy || !smtpOk || !wordingOk} title={!smtpOk ? 'Email is not set up on this platform.' : 'Emails the wording to your own address, with the plain form link'} onClick={() => void run('test')}>
        {busy === 'test' ? 'Sending…' : 'Send me a test'}
      </button>
    </div>
  );

  const body = (
    <>
      {error && <div className="ad-err">{error}</div>}
      <div className="fs-field">
        <label htmlFor="inv-who">1 · Who (one email per line)</label>
        <textarea
          id="inv-who" aria-label="Recipients" rows={5} className="fs-mono inv-who"
          placeholder={'dana@acme.com\nBo Lee <bo@acme.com>'}
          value={pasted} onChange={(e) => setPasted(e.target.value)}
        />
        <div className="hint inv-count">
          {people(count)}{parsed.invalid ? ` · ${parsed.invalid} line${parsed.invalid === 1 ? '' : 's'} without an address` : ''}{parsed.duplicates ? ` · ${parsed.duplicates} repeated` : ''}
          {' · '}or{' '}
          <label className="inv-csv">
            upload a CSV
            <input ref={fileRef} type="file" accept=".csv,text/csv" aria-label="Upload CSV"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setFiles((fs) => [...fs, f]); if (fileRef.current) fileRef.current.value = ''; }} />
          </label>
          {' '}with an email column and, if you like, a name (or first/last name) column
        </div>
        {files.length > 0 && (
          <ul className="inv-files">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`}>
                <span className="fs-mono">{f.name}</span>
                <button type="button" className="fc-x" aria-label={`Remove ${f.name}`} onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}>×</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="fs-field">
        <label htmlFor="inv-subject">2 · Subject</label>
        <input id="inv-subject" aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div className="fs-field">
        <label htmlFor="inv-message">Message</label>
        <textarea id="inv-message" aria-label="Message" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
        <div className="hint">{'{{name}}'} becomes each person’s name (or “there”). The link button and the sender line are added automatically.</div>
      </div>
      <div className="fs-field">
        <SectionLabel>3 · Reminders</SectionLabel>
        <ReminderSchedule value={days} onChange={setDays} idPrefix="inv-new" />
      </div>
      <div className="fs-consequence inv-nothing">
        Nothing sends until you press Send. Save as draft keeps the wording and the list, and opens the batch so you can send yourself a test first.
      </div>
    </>
  );

  if (inline) {
    return (
      <>
        <div className="inv-aside-head">
          <b>New invitation</b>
          <button className="fs-btn icon" title="Close" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="inv-aside-body">{body}</div>
        <div className="inv-aside-foot">{foot}</div>
      </>
    );
  }
  return (
    <Drawer title="New invitation" narrow onClose={onClose} className="inv-drawer" footer={foot}>
      {body}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// One batch.
// ---------------------------------------------------------------------------

function BatchDetail({ formId, batchId, formName, onBack }: {
  formId: number; batchId: number; formName: string; onBack: () => void;
}) {
  const toast = useToast();
  const [batch, setBatch] = useState<InvitationBatch | null>(null);
  const [recipients, setRecipients] = useState<InvitationRecipient[]>([]);
  const [reminders, setReminders] = useState<InvitationReminder[]>([]);
  const [counts, setCounts] = useState<InvitationBatchCounts | null>(null);
  const [window_, setWindow] = useState<SendWindow | null>(null);
  const [smtpOk, setSmtpOk] = useState(true);
  const [published, setPublished] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [days, setDays] = useState<number[]>([]);
  const [pasted, setPasted] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => getInvitationBatchApi(formId, batchId)
    .then((r) => {
      setBatch(r.batch); setRecipients(r.recipients); setReminders(r.reminders || []); setCounts(r.counts || null);
      setWindow(r.window); setSmtpOk(r.smtpConfigured); setPublished(r.published !== false);
      setSubject(r.batch.subject); setMessage(r.batch.message); setDays(r.batch.reminder_days || []);
    })
    .catch((e) => setError((e as Error).message));
  useEffect(() => { void load(); }, [formId, batchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn: () => Promise<unknown>, done: string | ((r: any) => string)) => {
    setBusy(true); setError('');
    try { const r = await fn(); toast(typeof done === 'function' ? done(r) : done); await load(); } catch (e) { setError((e as Error).message); }
    setBusy(false);
  };

  const addedToast = (r: { added: number; alreadyOnList: number; invalid: number }) =>
    `${r.added} added${r.alreadyOnList ? `, ${r.alreadyOnList} already on the list` : ''}${r.invalid ? `, ${r.invalid} unusable row${r.invalid === 1 ? '' : 's'}` : ''}.`;

  const onCsv = async (file: File) => {
    await act(() => uploadInvitationCsvApi(formId, batchId, file), addedToast);
    if (fileRef.current) fileRef.current.value = '';
  };
  const pastedParsed = useMemo(() => parsePastedRecipients(pasted), [pasted]);
  const addPasted = async () => {
    if (!pastedParsed.recipients.length) return;
    const blob = new Blob([recipientsToCsv(pastedParsed.recipients)], { type: 'text/csv' });
    await act(() => uploadInvitationCsvApi(formId, batchId, blob), addedToast);
    setPasted('');
  };

  /** The pending reminder per recipient, and how many went — for the status cell. */
  const reminderState = useMemo(() => {
    const queued = new Set<number>();
    for (const m of reminders) if (!m.sent_at && !m.failed_at) queued.add(m.recipient_id);
    return queued;
  }, [reminders]);

  if (error && !batch) return <div className="fs-card pad"><div className="ad-err">{error}</div></div>;
  if (!batch) return <div className="fs-card pad">Loading…</div>;
  const copyLocked = batch.status !== 'draft';
  const copyDirty = subject !== batch.subject || message !== batch.message;
  const scheduleDirty = JSON.stringify(days) !== JSON.stringify(batch.reminder_days || []);
  const scheduleBad = scheduleProblem(days);
  const c: InvitationBatchCounts = counts || {
    total: recipients.length,
    sent: recipients.filter((r) => r.message_id).length,
    failed: recipients.filter((r) => r.failed_at).length,
    pending: recipients.filter((r) => !r.sent_at && !r.failed_at).length,
    opened: recipients.filter((r) => r.first_opened_at).length,
    submitted: recipients.filter((r) => r.submitted_at).length,
    unfinished: recipients.filter((r) => r.message_id && !r.submitted_at).length,
    reminded: recipients.filter((r) => (r.reminder_count || 0) > 0).length,
    reminders_queued: reminderState.size,
  };
  const canRemind = batch.status !== 'draft' && smtpOk && published && c.unfinished > 0;

  const remindToast = (r: { queued: number; unfinished: number; skipped: number; paused: boolean }) =>
    !r.unfinished ? 'Nobody to remind — everyone the email reached has finished.'
      : !r.queued ? `Nobody reminded: ${people(r.skipped)} already had a reminder in the last day.`
        : `Reminding ${people(r.queued)} who haven’t finished.${r.paused ? ' They go out when the batch resumes.' : ' They go out at the platform pace.'}`;

  return (
    <div className="inv-page">
      <div className="inv-main">
      <p style={{ margin: '0 0 10px' }}>
        <a href="#back" onClick={(e) => { e.preventDefault(); onBack(); }}>← Invitations</a>
      </p>
      <div className="fs-page-head">
        <div className="text">
          <h2>{batch.subject} <Pill status={batch.status} /></h2>
          <div className="fs-page-sub">
            {when(batch.created_at)} · {people(c.total)}{batch.status === 'draft' ? ' · Send yourself a test, add the list, then start it.' : ''}
          </div>
        </div>
        <div className="inv-detail-actions">
          {(batch.status === 'draft' || batch.status === 'paused') && (
            <button className="fs-btn primary" disabled={busy || !smtpOk || !published || !recipients.length}
              title={!published ? 'Publish the form first.' : undefined}
              onClick={() => void act(() => startInvitationBatchApi(formId, batchId), START_TOAST)}>
              {batch.status === 'paused' ? 'Resume sending' : 'Start sending'}
            </button>
          )}
          {batch.status === 'sending' && (
            <button className="fs-btn primary" disabled={busy}
              onClick={() => void act(() => pauseInvitationBatchApi(formId, batchId), 'Paused.')}>
              Pause
            </button>
          )}
          {batch.status !== 'draft' && (
            <button className="fs-btn neutral" disabled={busy || !canRemind}
              title={!smtpOk ? 'Email is not set up on this platform.' : !published ? 'Publish the form first.' : !c.unfinished ? 'Everyone the email reached has finished.' : undefined}
              onClick={() => void act(() => remindInvitationBatchApi(formId, batchId), remindToast)}>
              Remind unfinished{c.unfinished ? ` · ${c.unfinished}` : ''}
            </button>
          )}
          <button className="fs-btn neutral" disabled={busy || !smtpOk}
            onClick={() => void act(() => testInvitationBatchApi(formId, batchId), 'Test sent to your own address.')}>
            Send me a test
          </button>
        </div>
      </div>
      {error && <div className="ad-err">{error}</div>}

      <section className="fs-card inv-card">
        <Bars c={c} />
        {(c.pending > 0 || c.failed > 0 || c.reminded > 0 || c.reminders_queued > 0) && (
          <div className="inv-note faint">
            {[
              c.pending > 0 ? `${c.pending} queued` : null,
              c.failed > 0 ? `${c.failed} failed` : null,
              c.reminded > 0 ? `${people(c.reminded)} reminded` : null,
              c.reminders_queued > 0 ? `${c.reminders_queued} reminder${c.reminders_queued === 1 ? '' : 's'} queued${batch.status === 'paused' ? ' (held while paused)' : ''}` : null,
            ].filter(Boolean).join(' · ')}
          </div>
        )}
      </section>

      <div className="la-grid">
        <section className="fs-card">
          <div className="fs-card-head">
            <h2>The email</h2>
            {copyLocked && <div className="hint">The wording locked when sending started, so everyone on the list gets the same words.</div>}
          </div>
          <div className="fs-card-body">
            <div className="fs-field">
              <label htmlFor="inv-edit-subject">Subject</label>
              <input id="inv-edit-subject" aria-label="Subject" value={subject} disabled={copyLocked} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="fs-field">
              <label htmlFor="inv-edit-message">Message</label>
              <textarea id="inv-edit-message" aria-label="Message" rows={7} value={message} disabled={copyLocked} onChange={(e) => setMessage(e.target.value)} />
              <div className="hint">{'{{name}}'} becomes each person’s name. Each email carries that person’s own link to “{formName}”.</div>
            </div>
            {!copyLocked && (
              <button className="fs-btn primary" disabled={busy || !copyDirty || !subject.trim() || !message.trim()}
                onClick={() => void act(() => updateInvitationBatchApi(formId, batchId, { subject, message }), 'Wording saved.')}>
                Save wording
              </button>
            )}
          </div>
        </section>

        <section className="fs-card">
          <div className="fs-card-head">
            <h2>Reminders</h2>
            <div className="hint">
              The schedule can change at any time — it is about the future. A reminder repeats the email above under a “Reminder:” subject, on a fresh private link.
            </div>
          </div>
          <div className="fs-card-body">
            <ReminderSchedule value={days} onChange={setDays} idPrefix="inv-edit" />
            {scheduleBad && scheduleDirty && <div className="ad-err" style={{ marginTop: 8 }}>{scheduleBad}</div>}
            <button className="fs-btn primary" style={{ marginTop: 12 }} disabled={busy || !scheduleDirty || !!scheduleBad}
              onClick={() => void act(() => updateInvitationBatchApi(formId, batchId, { reminderDays: days }), 'Reminder schedule saved.')}>
              Save schedule
            </button>
          </div>
        </section>
      </div>

      <section className="fs-card inv-recipients">
        <div className="inv-recipients-head">
          <h2>Recipients ({recipients.length})</h2>
          <span style={{ flex: 1 }} />
          <label className="fs-btn neutral" style={{ cursor: 'pointer' }}>
            Upload CSV…
            <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onCsv(f); }} />
          </label>
        </div>
        <p className="faint inv-recipients-hint">
          A CSV with an email column, and optionally a name (or first/last name) column — or paste people below, one per line.
        </p>
        <div className="inv-add-row">
          <textarea
            aria-label="Add people" rows={2} className="fs-mono inv-who"
            placeholder={'dana@acme.com\nBo Lee <bo@acme.com>'}
            value={pasted} onChange={(e) => setPasted(e.target.value)}
          />
          <button className="fs-btn neutral" disabled={busy || !pastedParsed.recipients.length} onClick={() => void addPasted()}>
            Add {pastedParsed.recipients.length ? people(pastedParsed.recipients.length) : 'people'}
          </button>
        </div>
        <table className="fs-table stack">
          <thead><tr><th>Email</th><th>Name</th><th>Status</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {recipients.map((r) => (
              <tr key={r.id}>
                <td data-label="Email">{r.email}</td>
                <td data-label="Name">{r.name || <span className="faint">—</span>}</td>
                <td data-label="Status">
                  {r.submitted_at ? <span className="fs-pill ok">completed</span>
                    : r.first_opened_at ? <span className="fs-pill identity">opened</span>
                      : r.message_id ? <span className="fs-pill neutral">sent</span>
                        : r.failed_at ? <span className="fs-pill danger" title={r.error || ''}>failed</span>
                          : r.sent_at ? <span className="fs-pill caution">sending</span>
                            : <span className="fs-pill neutral">queued</span>}
                  {((r.reminder_count || 0) > 0 || reminderState.has(r.id)) && (
                    <div className="fs-cell-sub faint">
                      {[
                        (r.reminder_count || 0) > 0 ? `reminded ×${r.reminder_count}` : null,
                        reminderState.has(r.id) ? 'reminder queued' : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td className="fs-cell-actions">
                  {!r.sent_at && !r.failed_at && (
                    <button className="fs-btn icon" title="Remove from list" aria-label={`Remove ${r.email}`}
                      onClick={() => void act(() => removeInvitationRecipientApi(formId, batchId, r.id), 'Removed.')}>✕</button>
                  )}
                </td>
              </tr>
            ))}
            {!recipients.length && <tr><td colSpan={4} className="faint">Nobody yet — paste people above or upload a CSV.</td></tr>}
          </tbody>
        </table>
      </section>
      {window_ && <PaceNote window={window_} />}
      </div>
    </div>
  );
}
